import React from "react";
import { Box, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import {
  faCommentSms,
  faEnvelope,
  faPhone,
  faTurnRight,
} from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";
import {
  PhlebotomyLocation,
  PhlebotomyFinderAction,
  formatPhoneDisplay,
  formatPhoneForTel,
} from "../types";
import BaseCard from "../components/BaseCard";
import {
  LocationName,
  LocationPhone,
  LocationTitleRow,
  LocationTypeRow,
  OutlineIconActionButton,
} from "../components/BaseCard.styles";
import PhlebotomyCardMetadata from "../components/PhlebotomyCardMetadata";
import {
  FinderCardActionsRow,
  FinderPrimaryActionButton,
  LocationDistance,
} from "./PhlebotomyLocationCard.styles";

export type LocationCardAction =
  | "location_phone"
  | "location_email"
  | "location_directions"
  | "location_schedule";

interface PhlebotomyLocationCardProps {
  location: PhlebotomyLocation;
  index: number;
  isSelected: boolean;
  onSelect: (index: number) => void;
  onButtonClick?: (
    type: LocationCardAction,
    location: PhlebotomyLocation,
  ) => void;
  onMobileSchedule?: () => void;
}

const preventCardSelectThen =
  (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    action();
  };

const PhlebotomyLocationCard: React.FC<PhlebotomyLocationCardProps> = ({
  location,
  index,
  isSelected,
  onSelect,
  onButtonClick,
  onMobileSchedule,
}) => {
  const { t, i18n } = useTranslation();
  const isSpanish = Boolean(i18n.resolvedLanguage?.startsWith("es"));

  const handleDirections = preventCardSelectThen(() => {
    onButtonClick?.("location_directions", location);
    const destination = encodeURIComponent(
      `${location.address}, ${location.city}, ${location.state} ${location.zipCode}`,
    );
    window.open(`https://maps.google.com/maps?daddr=${destination}`);
  });

  const handlePhoneCall = preventCardSelectThen(() => {
    if (!location.phone) return;
    onButtonClick?.("location_phone", location);
    window.open(formatPhoneForTel(location.phone));
  });

  const handleSchedule = preventCardSelectThen(() => {
    if (!location.schedulingUrl) return;
    onButtonClick?.("location_schedule", location);
    window.open(location.schedulingUrl, "_blank");
  });

  const finderActionLabel = (action: PhlebotomyFinderAction) =>
    t(`phlebotomyLocationFinder.card.actions.${action.type}`);

  const finderActionIcon = (action: PhlebotomyFinderAction) => {
    switch (action.type) {
      case "call":
        return faPhone;
      case "text":
        return faCommentSms;
      case "email":
        return faEnvelope;
      case "website":
        return faCalendar;
      case "directions":
        return faTurnRight;
      case "schedule_mobile_draw":
        return faCalendar;
      default: {
        const exhaustiveCheck: never = action.type;
        return exhaustiveCheck;
      }
    }
  };

  const finderTrackingAction = (
    action: PhlebotomyFinderAction,
  ): LocationCardAction => {
    if (action.type === "directions") return "location_directions";
    if (action.type === "email") return "location_email";
    if (action.type === "website" || action.type === "schedule_mobile_draw") {
      return "location_schedule";
    }
    return "location_phone";
  };

  const handleFinderAction = (action: PhlebotomyFinderAction) =>
    preventCardSelectThen(() => {
      if (action.type === "schedule_mobile_draw") {
        onButtonClick?.(finderTrackingAction(action), location);
        onMobileSchedule?.();
        return;
      }
      onButtonClick?.(finderTrackingAction(action), location);
      if (action.type === "call") {
        window.location.href = `tel:${action.target}`;
      } else if (action.type === "text") {
        window.location.href = `sms:${action.target}?body=${encodeURIComponent(
          t("phlebotomy.mobilePhlebotomy.smsMessage"),
        )}`;
      } else if (action.type === "email") {
        window.location.href = action.target;
      } else {
        window.open(action.target, "_blank");
      }
    });

  const orderedFinderActions = location.finderActions
    ? [
        ...location.finderActions.filter((action) => !action.isPrimary),
        ...location.finderActions.filter((action) => action.isPrimary),
      ]
    : [];

  const cardContent = (
    <Box sx={{ width: "100%" }}>
      <LocationTitleRow>
        <LocationName>{location.name}</LocationName>
        {location.distanceMiles != null && (
          <LocationDistance>
            {t("phlebotomyLocationFinder.card.distance", {
              distance: location.distanceMiles,
            })}
          </LocationDistance>
        )}
      </LocationTitleRow>
      {location.siteType && (
        <LocationTypeRow>
          <Typography variant="body2" color="text.secondary">
            {t(`phlebotomyLocationFinder.card.types.${location.siteType}`)}
          </Typography>
        </LocationTypeRow>
      )}
      {location.phone && (
        <LocationPhone>{formatPhoneDisplay(location.phone)}</LocationPhone>
      )}

      <PhlebotomyCardMetadata
        lines={location.metadata ?? []}
        plainLines={location.siteNotes}
        keyPrefix={`location-${location.name}`}
      />
    </Box>
  );

  const legacyCardActions = (
    <FinderCardActionsRow
      hasPrimaryAction={Boolean(location.phone)}
      isSpanish={isSpanish}
    >
      {location.schedulingUrl && (
        <OutlineIconActionButton
          variant="unity-outline-orange"
          aria-label={t("phlebotomyLocationFinder.card.schedule")}
          onClick={handleSchedule}
        >
          <FontAwesomeIcon icon={faCalendar} />
        </OutlineIconActionButton>
      )}
      <OutlineIconActionButton
        variant="unity-outline-orange"
        aria-label={t("phlebotomyLocationFinder.card.directions")}
        onClick={handleDirections}
      >
        <FontAwesomeIcon icon={faTurnRight} />
      </OutlineIconActionButton>
      {location.phone && (
        <FinderPrimaryActionButton
          variant="unity-primary"
          onClick={handlePhoneCall}
          startIcon={<FontAwesomeIcon icon={faPhone} />}
        >
          {t("phlebotomyLocationFinder.card.call")}
        </FinderPrimaryActionButton>
      )}
    </FinderCardActionsRow>
  );

  const catalogCardActions = orderedFinderActions.length ? (
    <FinderCardActionsRow
      isSpanish={isSpanish}
      hasPrimaryAction={orderedFinderActions.some((action) => action.isPrimary)}
    >
      {orderedFinderActions.map((action) =>
        action.isPrimary ? (
          <FinderPrimaryActionButton
            key={action.type}
            variant="unity-primary"
            onClick={handleFinderAction(action)}
            startIcon={<FontAwesomeIcon icon={finderActionIcon(action)} />}
          >
            {finderActionLabel(action)}
          </FinderPrimaryActionButton>
        ) : (
          <OutlineIconActionButton
            key={action.type}
            variant="unity-outline-orange"
            aria-label={finderActionLabel(action)}
            onClick={handleFinderAction(action)}
          >
            <FontAwesomeIcon icon={finderActionIcon(action)} />
          </OutlineIconActionButton>
        ),
      )}
    </FinderCardActionsRow>
  ) : null;

  return (
    <BaseCard
      isHighlighted={isSelected}
      isPreferred={location.isPreferred || location.siteType === "bto_psc"}
      onClick={() => onSelect(index)}
      actions={catalogCardActions ?? legacyCardActions}
      showBorder={false}
    >
      {cardContent}
    </BaseCard>
  );
};

export default PhlebotomyLocationCard;
